import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BadgeCheck, CalendarClock, Check, CircleAlert, CloudUpload, Gift, Gauge, Plus,
  Save, ShieldAlert, Sparkles, Trash2, WandSparkles,
} from 'lucide-react';
import type {
  AdminApi, DailyBenefitConfig, DailyBenefitEntry,
  Environment, LiveConfig, Platform,
} from '../api';
import { RewardBundleEditor } from '../components/RewardBundleEditor';
import { rewardDisplayName } from '../components/reward-presentation';
import { ClientReleaseSelect, ConfigIssues } from '../components/RewardConfigControls';
import { useClientRelease } from '../hooks/useClientRelease';

interface Props {
  api: AdminApi;
  gameId: string;
  environment: Environment;
  platform: Platform;
  notify: (message: string) => void;
}

const MODE_LABELS: Record<DailyBenefitConfig['secondPickMode'], string> = {
  reshuffle: '看广告后重洗一次',
  remaining: '保留原顺序，选择剩余箱子',
  reshuffle_remaining: '重洗后隐藏首选箱子',
  double_or_reshuffle: '看广告：奖励翻倍 / 重新洗牌',
};

export function DailyBenefitCenter(props: Props) {
  const [config, setConfig] = useState<LiveConfig | null>(null);
  const [benefit, setBenefit] = useState<DailyBenefitConfig | null>(null);
  const [activeVersion, setActiveVersion] = useState('未发布');
  const { releaseKey, releaseKeys, releaseError, retryReleases, setReleaseKey } = useClientRelease(props.api, props.gameId, props.environment);
  const [loadedReleaseKey, setLoadedReleaseKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'draft' | 'publish' | ''>('');
  const [dirty, setDirty] = useState(false);
  const [serverIssue, setServerIssue] = useState('');
  const loadSequence = useRef(0);

  const load = async () => {
    if (!releaseKey) return;
    const sequence = ++loadSequence.current;
    setLoading(true);
    setServerIssue('');
    try {
      const [versions, template] = await Promise.all([
        props.api.versions(props.gameId, props.environment, releaseKey),
        props.api.configTemplate(props.gameId),
      ]);
      if (sequence !== loadSequence.current) return;
      const next = structuredClone(versions.active?.payload ?? versions.versions[0]?.payload ?? template.payload);
      next.releaseVersion = nextRelease(next.releaseVersion);
      setLoadedReleaseKey(releaseKey);
      setConfig(next);
      setBenefit(next.activities.dailyBenefit ? structuredClone(next.activities.dailyBenefit) : null);
      setActiveVersion(versions.active?.releaseVersion ?? '未发布');
      setDirty(false);
    } catch (error) {
      if (sequence !== loadSequence.current) return;
      setServerIssue(error instanceof Error ? error.message : '每日奖励配置读取失败');
      props.notify(error instanceof Error ? error.message : '每日奖励配置读取失败');
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  };

  useEffect(() => { void load(); return () => { loadSequence.current += 1; }; }, [props.gameId, props.environment, releaseKey]);

  const issues = useMemo(() => {
    if (!benefit || !config) return ['服务端尚未配置每日奖励'];
    return validateDailyBenefit(benefit, config);
  }, [benefit, config]);

  const ordinaryWeight = useMemo(() => benefit?.ordinaryEntries.reduce((sum, entry) => sum + entry.weight, 0) ?? 0, [benefit]);
  const rareWeight = useMemo(() => benefit?.rareEntries.reduce((sum, entry) => sum + entry.weight, 0) ?? 0, [benefit]);

  const updateBenefit = (updater: (draft: DailyBenefitConfig) => void) => {
    setBenefit((current) => {
      if (!current) return current;
      const draft = structuredClone(current);
      updater(draft);
      return draft;
    });
    setDirty(true);
    setServerIssue('');
  };

  const saveDraft = async (activate: boolean) => {
    if (busy || !config || !benefit || issues.length) return;
    if (activate && (!releaseKey || loadedReleaseKey !== releaseKey)) return;
    setBusy(activate ? 'publish' : 'draft');
    setServerIssue('');
    try {
      const payload = structuredClone(config);
      payload.activities.dailyBenefit = structuredClone(benefit);
      const result = await props.api.createDraft(
        props.gameId,
        `每日奖励 · ${benefit.configVersion}`,
        props.environment,
        payload,
      );
      if (!result.draft.validation.valid) {
        throw new Error(`服务端校验未通过：${result.draft.validation.issues.length} 项问题`);
      }
      if (activate) {
        await props.api.publishDraft(props.gameId, result.draft.draftId, true, releaseKey);
        setActiveVersion(payload.releaseVersion);
        setConfig({ ...payload, releaseVersion: nextRelease(payload.releaseVersion) });
      }
      setDirty(false);
      props.notify(activate ? '每日奖励配置已发布，只影响该 Release Key 的新局' : '每日奖励草稿已保存并通过校验');
    } catch (error) {
      const message = error instanceof Error ? error.message : '每日奖励配置保存失败';
      setServerIssue(message);
      props.notify(message);
    } finally {
      setBusy('');
    }
  };

  if (releaseError) return <div className="page"><div className="empty-state" role="alert">{releaseError}<button className="secondary-button" onClick={retryReleases}>重新读取版本</button></div></div>;
  if (serverIssue && loadedReleaseKey !== releaseKey && !loading) return <div className="page"><div className="empty-state" role="alert">{serverIssue}<button className="secondary-button" onClick={() => void load()}>重试读取配置</button></div></div>;
  if (!releaseKey || loading || loadedReleaseKey !== releaseKey) return <div className="page"><div className="loading-card">正在读取每日奖励配置…</div></div>;
  if (!benefit || !config) return <div className="page"><div className="empty-state">当前环境尚未配置每日奖励，请先在服务端创建 `activities.dailyBenefit`。</div></div>;

  return <div className="page daily-benefit-page">
    <section className="page-intro daily-benefit-intro">
      <div>
        <p className="eyebrow">DAILY BENEFIT / NINE BOX CONTRACT</p>
        <h2>每日奖励</h2>
        <span>配置九个奖励箱、权重与看广告后的第二出口；发布后只影响指定 Release Key 的新局。</span>
      </div>
      <div className="intro-actions daily-benefit-actions">
      <ClientReleaseSelect value={releaseKey} options={releaseKeys} disabled={Boolean(busy)} onChange={value => {
        if (!dirty || window.confirm('切换版本会放弃当前未保存改动，确定切换吗？')) { setLoading(true); setReleaseKey(value); }
      }} />
      <span className={`draft-state ${dirty ? 'dirty' : ''}`}>{dirty ? '有未保存改动' : '与草稿同步'}</span>
        <button className="secondary-button" disabled={Boolean(busy) || issues.length > 0} onClick={() => void saveDraft(false)}><Save size={16} />{busy === 'draft' ? '保存中…' : '保存草稿'}</button>
        <button className="primary-button compact" disabled={Boolean(busy) || !releaseKey || loadedReleaseKey !== releaseKey || issues.length > 0} onClick={() => void saveDraft(true)}><CloudUpload size={16} />{busy === 'publish' ? '发布中…' : `发布到${envName(props.environment)}`}</button>
      </div>
    </section>

    <ConfigIssues issues={issues} serverIssue={serverIssue} />

    <section className="daily-benefit-status">
      <div className="daily-benefit-status-main"><span className="daily-benefit-status-icon"><Gift size={19} /></span><div><strong>{benefit.displayName}</strong><span>{benefit.activityId} · 配置版本 {benefit.configVersion}</span></div><label className="switch"><input type="checkbox" checked={benefit.enabled} onChange={(event) => updateBenefit((draft) => { draft.enabled = event.target.checked; })} /><i /></label></div>
      <div className="daily-benefit-status-meta"><span>当前生效</span><strong>{activeVersion}</strong><i /><span>下次生效</span><strong>{config.releaseVersion}</strong></div>
    </section>

    <section className="daily-benefit-metrics">
      <Metric icon={<BoxesGlyph />} label="普通奖励池" value={`${benefit.ordinaryEntries.length} 个`} detail={`权重总和 ${ordinaryWeight}`} />
      <Metric icon={<Sparkles size={18} />} label="稀有奖励池" value={`${benefit.rareEntries.length} 个`} detail={`权重总和 ${rareWeight}`} tone="orange" />
      <Metric icon={<Gauge size={18} />} label="首选后流程" value={benefit.secondPickMode === 'double_or_reshuffle' ? '双出口' : '重洗'} detail={MODE_LABELS[benefit.secondPickMode]} tone="yellow" />
      <Metric icon={<BadgeCheck size={18} />} label="发布合同" value={benefit.enabled ? '启用' : '停用'} detail={`生效于 ${formatDate(benefit.effectiveFrom)}`} tone="blue" />
    </section>

    <fieldset className="daily-benefit-editor" disabled={Boolean(busy)}><div className="daily-benefit-grid">
      <main>
        <section className="panel daily-benefit-contract-panel">
          <header><div><h3>活动合同</h3><p>广告、功能解锁与第二次选择策略均由服务端版本化校验。</p></div><CalendarClock size={18} /></header>
          <div className="daily-benefit-contract-form">
            <Field label="活动名称" value={benefit.displayName} onChange={(value) => updateBenefit((draft) => { draft.displayName = value; })} />
            <Field label="活动 ID（稳定引用）" value={benefit.activityId} onChange={(value) => updateBenefit((draft) => { draft.activityId = value; })} mono />
            <Field label="配置版本" value={benefit.configVersion} onChange={(value) => updateBenefit((draft) => { draft.configVersion = value; })} mono />
            <SelectField label="入口功能" value={benefit.requiredFeatureId} options={config.homeFeatures.map((feature) => ({ value: feature.featureId, label: `${feature.displayName} · ${feature.featureId}` }))} onChange={(value) => updateBenefit((draft) => { draft.requiredFeatureId = value; })} />
            <SelectField label="首选后策略" value={benefit.secondPickMode} options={Object.entries(MODE_LABELS).map(([value, label]) => ({ value, label }))} onChange={(value) => updateBenefit((draft) => { draft.secondPickMode = value as DailyBenefitConfig['secondPickMode']; })} />
            <Field label="生效时间" type="datetime-local" value={toDateTimeLocal(benefit.effectiveFrom)} onChange={(value) => updateBenefit((draft) => { draft.effectiveFrom = fromDateTimeLocal(value); })} />
            <SelectField label="重洗广告位" value={benefit.adPlacementId} options={adOptions(config, benefit.adPlacementId)} onChange={(value) => updateBenefit((draft) => { draft.adPlacementId = value; })} />
            <SelectField label="翻倍广告位（可选）" value={benefit.doubleAdPlacementId ?? ''} options={[{ value: '', label: '不提供翻倍出口' }, ...adOptions(config, benefit.doubleAdPlacementId)]} onChange={(value) => updateBenefit((draft) => { draft.doubleAdPlacementId = value || undefined; })} />
          </div>
          <div className="daily-benefit-contract-note"><ShieldAlert size={17} /><div><strong>发布前会校验引用和奖励结构</strong><span>稳定 ID、广告位、入口功能、权重、奖励类型和第二出口策略必须与服务端 schema 一致；发布使用现有草稿审计和 Release Key 隔离。</span></div></div>
        </section>

        <EntryPool title="普通奖励池" eyebrow="8 个普通箱位 · 按权重抽取" entries={benefit.ordinaryEntries} totalWeight={ordinaryWeight} accent="green" onChange={(index, entry) => updateBenefit((draft) => { draft.ordinaryEntries[index] = entry; })} onAdd={() => updateBenefit((draft) => { draft.ordinaryEntries.push(newEntry('ordinary', draft.ordinaryEntries.length + 1)); })} onRemove={(index) => updateBenefit((draft) => { if (draft.ordinaryEntries.length > 1) draft.ordinaryEntries.splice(index, 1); })} />
        <EntryPool title="稀有奖励池" eyebrow="1 个稀有箱位 · 独立权重池" entries={benefit.rareEntries} totalWeight={rareWeight} accent="orange" onChange={(index, entry) => updateBenefit((draft) => { draft.rareEntries[index] = entry; })} onAdd={() => updateBenefit((draft) => { draft.rareEntries.push(newEntry('rare', draft.rareEntries.length + 1)); })} onRemove={(index) => updateBenefit((draft) => { if (draft.rareEntries.length > 1) draft.rareEntries.splice(index, 1); })} />
      </main>
      <aside className="daily-benefit-rail">
        <article className="daily-benefit-preview-card"><div className="preview-orbit"><span /><span /><span /><Gift size={23} /></div><p className="eyebrow">PLAYER-FACING CONTRACT</p><h3>九箱一次选择</h3><p>首次免费打开九个关闭箱子中的一个；若启用双出口，首选结果会进入“奖励翻倍 / 重新洗牌”的决策面。</p><div className="preview-flow"><FlowStep index="01" title="首次免费" detail="九箱 · 选一" /><FlowStep index="02" title="看广告" detail={benefit.secondPickMode === 'double_or_reshuffle' ? '翻倍 / 重洗' : '按策略继续'} /><FlowStep index="03" title="最终领取" detail="写入奖励台账" /></div></article>
        <article className="daily-benefit-guard-card"><CircleAlert size={18} /><div><strong>{issues.length ? `${issues.length} 项待修正` : '配置可发布'}</strong><span>{issues.length ? '请先修复表单中的问题，发布按钮会保持禁用。' : '服务端仍会在保存草稿时做最终 schema 校验。'}</span></div></article>
        {issues.length > 0 && <ul className="daily-benefit-issues">{issues.map((issue) => <li key={issue}><CircleAlert size={13} />{issue}</li>)}</ul>}
        {serverIssue && <div className="form-error daily-benefit-server-error">{serverIssue}</div>}
        <article className="daily-benefit-rule-card"><WandSparkles size={18} /><div><strong>运营提示</strong><p>奖励显示名可以随时调整；已发布的 entryId 建议保持不变，避免历史数据和报表无法对齐。</p></div></article>
      </aside>
    </div></fieldset>
  </div>;
}

function EntryPool({ title, eyebrow, entries, totalWeight, accent, onChange, onAdd, onRemove }: {
  title: string;
  eyebrow: string;
  entries: DailyBenefitEntry[];
  totalWeight: number;
  accent: 'green' | 'orange';
  onChange: (index: number, entry: DailyBenefitEntry) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}) {
  return <section className={`panel daily-benefit-pool-panel ${accent}`}>
    <header><div><p className="eyebrow">{eyebrow}</p><h3>{title}</h3></div><button className="secondary-button" disabled={entries.length >= (accent === 'green' ? 32 : 16)} onClick={onAdd}><Plus size={15} />增加奖励</button></header>
    <div className="daily-benefit-pool-list">{entries.map((entry, index) => <EntryRow key={index} entry={entry} index={index} totalWeight={totalWeight} onChange={onChange} onRemove={onRemove} canRemove={entries.length > 1} />)}</div>
  </section>;
}

function EntryRow({ entry, index, totalWeight, onChange, onRemove, canRemove }: {
  entry: DailyBenefitEntry;
  index: number;
  totalWeight: number;
  onChange: (index: number, entry: DailyBenefitEntry) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}) {
  const percentage = totalWeight > 0 ? `${((entry.weight / totalWeight) * 100).toFixed(1)}%` : '—';
  const change = (patch: Partial<DailyBenefitEntry>) => onChange(index, { ...entry, ...patch });
  return <article className="daily-benefit-entry-row">
    <div className="entry-index">{String(index + 1).padStart(2, '0')}</div>
    <div className="entry-fields">
      <label><span>奖励名称 · 随内容自动更新</span><input value={entry.displayName} onChange={(event) => change({ displayName: event.target.value })} /></label>
      <label><span title="用于关联已有记录；更换奖励时保持不变">稳定 ID · 无需随道具修改</span><input className="mono-input" value={entry.entryId} onChange={(event) => change({ entryId: event.target.value.trim() })} /></label>
      <label className="weight-field"><span>权重</span><div><input type="number" min={1} max={1000000} value={entry.weight} onChange={(event) => change({ weight: Number(event.target.value) })} /><em>{percentage}</em></div></label>
    </div>
    <RewardBundleEditor bundle={entry.reward} onChange={(reward) => change({ reward, displayName: rewardDisplayName(reward) })} />
    <button className="icon-button entry-delete" disabled={!canRemove} onClick={() => onRemove(index)} aria-label={`删除 ${entry.displayName}`}><Trash2 size={16} /></button>
  </article>;
}

function Metric({ icon, label, value, detail, tone = 'green' }: { icon: ReactNode; label: string; value: string; detail: string; tone?: string }) {
  return <article className={`daily-benefit-metric ${tone}`}><span className="metric-icon">{icon}</span><span>{label}</span><strong>{value}</strong><p>{detail}</p></article>;
}

function Field({ label, value, onChange, type = 'text', mono = false }: { label: string; value: string; onChange: (value: string) => void; type?: string; mono?: boolean }) {
  return <label className="daily-benefit-field"><span>{label}</span><input className={mono ? 'mono-input' : ''} type={type} value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (value: string) => void }) {
  return <label className="daily-benefit-field"><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>;
}

function FlowStep({ index, title, detail }: { index: string; title: string; detail: string }) {
  return <div className="flow-step"><span>{index}</span><div><strong>{title}</strong><small>{detail}</small></div><Check size={14} /></div>;
}

function BoxesGlyph() { return <span className="boxes-glyph"><i /><i /><i /></span>; }

function validateDailyBenefit(benefit: DailyBenefitConfig, config: LiveConfig): string[] {
  const issues: string[] = [];
  const stableId = /^[a-z0-9][a-z0-9_-]{1,63}$/;
  if (!benefit.displayName.trim()) issues.push('活动名称不能为空');
  if (!stableId.test(benefit.activityId)) issues.push('活动 ID 必须是稳定的小写字母、数字、下划线或短横线');
  if (!benefit.configVersion.trim() || benefit.configVersion.length > 64) issues.push('配置版本需要 1–64 个字符');
  if (!benefit.requiredFeatureId || !config.homeFeatures.some((feature) => feature.featureId === benefit.requiredFeatureId)) issues.push('入口功能引用不存在');
  if (!benefit.adPlacementId || !config.ads.some((ad) => ad.placementId === benefit.adPlacementId)) issues.push('重洗广告位引用不存在');
  if (benefit.doubleAdPlacementId && !config.ads.some((ad) => ad.placementId === benefit.doubleAdPlacementId)) issues.push('翻倍广告位引用不存在');
  if (benefit.secondPickMode === 'double_or_reshuffle' && !benefit.doubleAdPlacementId) issues.push('双出口策略必须配置翻倍广告位');
  if (!benefit.effectiveFrom || Number.isNaN(new Date(benefit.effectiveFrom).getTime())) issues.push('生效时间格式不正确');
  const entries = [...benefit.ordinaryEntries, ...benefit.rareEntries];
  if (!benefit.ordinaryEntries.length || !benefit.rareEntries.length) issues.push('普通奖励池和稀有奖励池都至少需要一项');
  if (new Set(entries.map((entry) => entry.entryId)).size !== entries.length) issues.push('普通和稀有奖励池的稳定 ID 不能重复');
  entries.forEach((entry, index) => {
    const label = entry.displayName || `第 ${index + 1} 项`;
    if (!stableId.test(entry.entryId)) issues.push(`${label} 的稳定 ID 格式不正确`);
    if (!entry.displayName.trim() || entry.displayName.length > 64) issues.push(`${label} 的奖励名称需要 1–64 个字符`);
    if (!Number.isInteger(entry.weight) || entry.weight < 1 || entry.weight > 1000000) issues.push(`${label} 的权重必须是 1–1000000 的整数`);
    if (!entry.reward.items.length) issues.push(`${label} 至少需要一项发放内容`);
    const rewardKeys = entry.reward.items.map((item) => item.type === 'tool_entitlement' ? `${item.type}:${item.toolId}` : item.type);
    if (new Set(rewardKeys).size !== rewardKeys.length) issues.push(`${label} 的同类奖励请合并数量，不要重复添加`);
    entry.reward.items.forEach((item) => {
      const amount = item.type === 'tool_entitlement' ? item.count : item.amount;
      const max = item.type === 'tool_entitlement' ? 99 : 1000000;
      if (!Number.isInteger(amount) || amount < 1 || amount > max) issues.push(`${label} 的奖励数量必须是 1–${max} 的整数`);
    });
  });
  return [...new Set(issues)];
}

function adOptions(config: LiveConfig, current?: string) {
  const options = config.ads.map((ad) => ({ value: ad.placementId, label: `${ad.displayName} · ${ad.placementId}` }));
  return current && !options.some((option) => option.value === current)
    ? [{ value: current, label: `未知广告位 · ${current}` }, ...options]
    : options;
}

function newEntry(pool: 'ordinary' | 'rare', index: number): DailyBenefitEntry {
  return { entryId: `daily_${pool}_${index}`, displayName: pool === 'rare' ? '新稀有奖励' : '新普通奖励', weight: 10, reward: { items: [{ type: 'fish_dry', amount: 20 }] } };
}

function nextRelease(value: string) { const match = value.match(/^(.*?)(\d+)$/); return match ? `${match[1]}${Number(match[2]) + 1}` : `${value}-next`; }
function envName(value: Environment) { return value === 'production' ? '正式环境' : value === 'test' ? '测试环境' : '体验环境'; }
function formatDate(value: string) { const date = new Date(value); return Number.isNaN(date.getTime()) ? '未设置' : date.toLocaleString('zh-CN', { dateStyle: 'short', timeStyle: 'short' }); }
function toDateTimeLocal(value: string) { const date = new Date(value); if (Number.isNaN(date.getTime())) return ''; const offset = date.getTimezoneOffset() * 60_000; return new Date(date.getTime() - offset).toISOString().slice(0, 16); }
function fromDateTimeLocal(value: string) { const date = new Date(value); return Number.isNaN(date.getTime()) ? value : date.toISOString(); }
